import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TitleIcon from '@mui/icons-material/Title';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import { convertFromHTML, convertFromRaw, convertToRaw, ContentState, Editor, EditorState, Modifier, RichUtils, AtomicBlockUtils } from 'draft-js';
import 'draft-js/dist/Draft.css';
import { useWebSocket } from '../WebSocketContext';

const INLINE_STYLE_MAP = {
  BOLD: { tag: 'strong' },
  ITALIC: { tag: 'em' },
  UNDERLINE: { tag: 'u' },
  STRIKETHROUGH: { tag: 's' },
  CODE: { tag: 'code' },
};

const BLOCK_TAG_MAP = {
  'header-one': 'h1',
  'header-two': 'h2',
  'header-three': 'h3',
  'header-four': 'h4',
  'header-five': 'h5',
  'header-six': 'h6',
  blockquote: 'blockquote',
  'unstyled': 'p',
};

const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => (
  {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]
));

const styleMatches = (styleSet, prefix) => Array.from(styleSet).filter((style) => style.startsWith(prefix));

const getStylesAtIndex = (ranges, index) => {
  const styleSet = new Set();
  for (const range of ranges) {
    if (index >= range.offset && index < range.offset + range.length) {
      styleSet.add(range.style);
    }
  }
  return styleSet;
};

const getBoundaryEnd = (ranges, index, currentEnd) => {
  let end = currentEnd;
  for (const range of ranges) {
    if (range.offset > index) {
      end = Math.min(end, range.offset);
    } else if (range.offset + range.length > index) {
      end = Math.min(end, range.offset + range.length);
    }
  }
  return end;
};

const getRangeAtIndex = (ranges, index) => {
  for (const range of ranges) {
    if (index >= range.offset && index < range.offset + range.length) {
      return range;
    }
  }
  return null;
};

const getInlineStyleWrappers = (styleSet) => {
  const wrappers = [];

  Object.entries(INLINE_STYLE_MAP).forEach(([styleName, config]) => {
    if (styleSet.has(styleName)) {
      wrappers.push({ open: `<${config.tag}>`, close: `</${config.tag}>` });
    }
  });

  styleMatches(styleSet, 'COLOR-').forEach((style) => {
    const color = style.slice('COLOR-'.length);
    wrappers.push({ open: `<span style="color:${escapeHtml(color)}">`, close: '</span>' });
  });

  styleMatches(styleSet, 'BG-').forEach((style) => {
    const color = style.slice('BG-'.length);
    wrappers.push({ open: `<span style="background-color:${escapeHtml(color)}">`, close: '</span>' });
  });

  return wrappers;
};

const htmlFromRaw = (rawContent) => {
  const blocks = rawContent.blocks || [];
  const entityMap = rawContent.entityMap || {};
  const parts = [];
  let activeListType = null;

  const closeList = () => {
    if (activeListType === 'unordered-list-item') {
      parts.push('</ul>');
    } else if (activeListType === 'ordered-list-item') {
      parts.push('</ol>');
    }
    activeListType = null;
  };

  const renderInlineRuns = (block) => {
    const text = block.text || '';
    if (!text) {
      return '<br />';
    }

    const ranges = [];
    let cursor = 0;

    while (cursor < text.length) {
      const styleSet = getStylesAtIndex(block.inlineStyleRanges, cursor);
      const entityRange = getRangeAtIndex(block.entityRanges, cursor);
      const entityKey = entityRange ? entityRange.key : null;

      let end = getBoundaryEnd(block.inlineStyleRanges, cursor, text.length);
      end = getBoundaryEnd(block.entityRanges, cursor, end);

      const chunk = escapeHtml(text.slice(cursor, end)).replace(/\n/g, '<br />');
      const wrappers = getInlineStyleWrappers(styleSet);
      let rendered = chunk;
      wrappers.forEach(({ open, close }) => {
        rendered = `${open}${rendered}${close}`;
      });

      if (entityKey !== null && entityMap[String(entityKey)]) {
        const entity = entityMap[String(entityKey)];
        if (entity.type === 'LINK') {
          const href = escapeHtml(entity.data?.url || '#');
          rendered = `<a href="${href}" target="_blank" rel="noreferrer">${rendered}</a>`;
        }
      }

      ranges.push(rendered);
      cursor = end;
    }

    return ranges.join('');
  };

  blocks.forEach((block) => {
    if (block.type === 'unordered-list-item' || block.type === 'ordered-list-item') {
      if (activeListType !== block.type) {
        closeList();
        parts.push(block.type === 'unordered-list-item' ? '<ul>' : '<ol>');
        activeListType = block.type;
      }
      parts.push(`<li>${renderInlineRuns(block)}</li>`);
      return;
    }

    closeList();

    if (block.type === 'atomic') {
      const entityKey = block.entityRanges?.[0]?.key;
      const entity = entityKey !== undefined && entityKey !== null ? entityMap[String(entityKey)] : null;
      if (entity?.type === 'IMAGE') {
        const src = escapeHtml(entity.data?.src || '');
        const alt = escapeHtml(entity.data?.alt || '');
        parts.push(`<p><img src="${src}" alt="${alt}" style="max-width:100%;height:auto;" /></p>`);
      }
      return;
    }

    const tagName = BLOCK_TAG_MAP[block.type] || 'p';
    parts.push(`<${tagName}>${renderInlineRuns(block)}</${tagName}>`);
  });

  closeList();
  return parts.join('');
};

const htmlToEditorState = (html) => {
  if (!html) {
    return EditorState.createEmpty();
  }

  try {
    const hasImages = /<img\b/i.test(String(html));
    if (!hasImages) {
      const blocksFromHTML = convertFromHTML(html);
      if (!blocksFromHTML?.contentBlocks) {
        return EditorState.createEmpty();
      }
      const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
      return EditorState.createWithContent(contentState);
    }

    if (typeof DOMParser === 'undefined') {
      const blocksFromHTML = convertFromHTML(html);
      if (!blocksFromHTML?.contentBlocks) {
        return EditorState.createEmpty();
      }
      const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
      return EditorState.createWithContent(contentState);
    }

    const parser = new DOMParser();
    const document = parser.parseFromString(String(html), 'text/html');
    const entityMap = {};
    const blocks = [];
    let entityCounter = 0;
    let blockCounter = 0;

    const nextBlockKey = () => `b${blockCounter += 1}`;
    const nextEntityKey = () => {
      const key = entityCounter;
      entityCounter += 1;
      return key;
    };

    const appendImageBlock = (imageElement) => {
      const src = imageElement.getAttribute('src') || '';
      if (!src) {
        return;
      }
      const entityKey = nextEntityKey();
      entityMap[String(entityKey)] = {
        type: 'IMAGE',
        mutability: 'IMMUTABLE',
        data: {
          src,
          alt: imageElement.getAttribute('alt') || 'image',
        },
      };
      blocks.push({
        key: nextBlockKey(),
        text: ' ',
        type: 'atomic',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [{ offset: 0, length: 1, key: entityKey }],
        data: {},
      });
    };

    const appendTextBlock = (element) => {
      const text = String(element.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) {
        return;
      }
      const blockTypeMap = {
        P: 'unstyled',
        DIV: 'unstyled',
        H1: 'header-one',
        H2: 'header-two',
        H3: 'header-three',
        H4: 'header-four',
        H5: 'header-five',
        H6: 'header-six',
        BLOCKQUOTE: 'blockquote',
        LI: 'unordered-list-item',
      };
      blocks.push({
        key: nextBlockKey(),
        text,
        type: blockTypeMap[element.tagName] || 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      });
    };

    Array.from(document.body.children).forEach((element) => {
      const images = Array.from(element.querySelectorAll('img'));
      if (images.length > 0) {
        images.forEach((imageElement) => appendImageBlock(imageElement));
        const textOnly = String(element.textContent || '').replace(/\s+/g, '').replace(/\u00a0/g, '');
        if (textOnly && images.length === 0) {
          appendTextBlock(element);
        }
        return;
      }

      appendTextBlock(element);
    });

    if (!blocks.length) {
      return EditorState.createEmpty();
    }

    return EditorState.createWithContent(convertFromRaw({ blocks, entityMap }));
  } catch (error) {
    return EditorState.createEmpty();
  }
};

const getMimeTypeFromFilename = (filename) => {
  const extension = String(filename || '').toLowerCase().split('.').pop();
  switch (extension) {
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    case 'svg':
      return 'image/svg+xml';
    case 'tif':
    case 'tiff':
      return 'image/tiff';
    default:
      return 'image/jpeg';
  }
};

const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
  try {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  } catch (error) {
    reject(error);
  }
});

const ImageBlock = ({ block, contentState }) => {
  const entityKey = block.getEntityAt(0);
  if (!entityKey) {
    return null;
  }

  let entity = null;
  try {
    entity = contentState.getEntity(entityKey);
  } catch (error) {
    entity = null;
  }

  const src = entity?.getData?.()?.src || '';
  const alt = entity?.getData?.()?.alt || 'image';

  if (!src) {
    return (
      <Box
        component="div"
        sx={{
          my: 1,
          p: 1,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          color: 'text.secondary',
          fontStyle: 'italic',
        }}
      >
        Image placeholder
      </Box>
    );
  }

  return (
    <Box
      component="div"
      sx={{
        my: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: '8px',
        }}
      />
    </Box>
  );
};

const RichTextDraftEditor = forwardRef(({
  value = '',
  onChange,
  placeholder = '',
  minHeight = '220px',
  onBrowseImage,
}, ref) => {
  const [editorState, setEditorState] = useState(() => htmlToEditorState(value));
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [assetUrl, setAssetUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState('');
  const editorRef = useRef(null);
  const imageFileInputRef = useRef(null);
  const pendingImageRequestRef = useRef(null);
  const pendingImageChunksRef = useRef([]);
  const pendingImageStreamActiveRef = useRef(false);
  const lastExternalValueRef = useRef(value);
  const { subscribeToMessages, ws } = useWebSocket();

  useEffect(() => {
    if (value === lastExternalValueRef.current) {
      return;
    }
    lastExternalValueRef.current = value;
    setEditorState(htmlToEditorState(value));
  }, [value]);

  const emitChange = (nextState) => {
    setEditorState(nextState);
    const htmlValue = htmlFromRaw(convertToRaw(nextState.getCurrentContent()));
    lastExternalValueRef.current = htmlValue;
    onChange?.(htmlValue);
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const clearPendingImageRequest = useCallback(() => {
    pendingImageRequestRef.current = null;
    pendingImageChunksRef.current = [];
    pendingImageStreamActiveRef.current = false;
    setImageLoading(false);
  }, []);

  const handleLocalImageFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setAssetUrl(reader.result);
        setImageDialogOpen(true);
      }
    };
    reader.onerror = () => {
      setAssetUrl('');
    };
    reader.readAsDataURL(file);
  };

  const triggerLocalImagePicker = () => {
    imageFileInputRef.current?.click();
  };

  const requestImageFromServer = useCallback((sourcePath) => {
    const nextSource = String(sourcePath || '').trim();
    if (!nextSource) {
      return;
    }

    const lowerSource = nextSource.toLowerCase();
    if (lowerSource.startsWith('data:') || lowerSource.startsWith('blob:') || lowerSource.startsWith('http://') || lowerSource.startsWith('https://')) {
      setAssetUrl(nextSource);
      setImageError('');
      setImageLoading(false);
      setImageDialogOpen(true);
      return;
    }

    pendingImageRequestRef.current = nextSource;
    pendingImageChunksRef.current = [];
    pendingImageStreamActiveRef.current = false;
    setAssetUrl('');
    setImageError('');
    setImageLoading(true);
    setImageDialogOpen(true);

    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        command: 'openfile',
        path: nextSource,
        download: true,
      }));
    }
  }, [ws]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(['openfile'], (parsedMessage) => {
      if (!pendingImageRequestRef.current) {
        return;
      }

      const data = parsedMessage.data || {};
      const status = data.status || '';
      const expectedFilename = pendingImageRequestRef.current.split(/[\\/]/).pop().toLowerCase();

      if (status === 'file_stream_start') {
        pendingImageStreamActiveRef.current = true;
        pendingImageChunksRef.current = [];
      } else if (status === 'file_stream_chunk') {
        if (!pendingImageRequestRef.current) {
          return;
        }
        if (!data.chunk_data) {
          return;
        }
        try {
          const binaryString = atob(data.chunk_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let index = 0; index < binaryString.length; index += 1) {
            bytes[index] = binaryString.charCodeAt(index);
          }
          pendingImageChunksRef.current.push(bytes);
        } catch (error) {
          setImageError('Failed to decode image data');
          unsubscribe();
          clearPendingImageRequest();
        }
      } else if (status === 'file_stream_complete') {
        if (!pendingImageRequestRef.current) {
          return;
        }
        const chunks = pendingImageChunksRef.current.filter(Boolean);
        if (!chunks.length) {
          setImageError('No image data received');
          unsubscribe();
          clearPendingImageRequest();
          return;
        }

        const blob = new Blob(chunks, { type: getMimeTypeFromFilename(expectedFilename) });
        readBlobAsDataUrl(blob)
          .then((dataUrl) => {
            if (typeof dataUrl === 'string') {
              setAssetUrl(dataUrl);
              setImageError('');
            } else {
              setImageError('Failed to convert image to preview');
            }
          })
          .catch(() => {
            setImageError('Failed to convert image to preview');
          })
          .finally(() => {
            unsubscribe();
            clearPendingImageRequest();
          });
      } else if (status === 'error') {
        if (!pendingImageRequestRef.current) {
          return;
        }
        setImageError(data.message || 'Failed to load image');
        unsubscribe();
        clearPendingImageRequest();
      }
    });

    return unsubscribe;
  }, [clearPendingImageRequest, subscribeToMessages]);

  const insertImage = () => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', {
      src: assetUrl.trim(),
      alt: 'image',
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    let nextState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    nextState = AtomicBlockUtils.insertAtomicBlock(nextState, entityKey, ' ');
    emitChange(nextState);
    setImageDialogOpen(false);
    setAssetUrl('');
    setTimeout(focusEditor, 0);
    if (selection) {
      // Keep the current selection so repeated inserts are predictable.
      setEditorState((current) => EditorState.forceSelection(current, selection));
    }
  };

  const insertLink = () => {
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      setLinkDialogOpen(false);
      setLinkUrl('');
      return;
    }
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
      url: linkUrl.trim(),
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const nextContentState = Modifier.applyEntity(
      contentStateWithEntity,
      selection,
      entityKey,
    );
    emitChange(EditorState.push(editorState, nextContentState, 'apply-entity'));
    setLinkDialogOpen(false);
    setLinkUrl('');
    setTimeout(focusEditor, 0);
  };

  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      const selection = editorState.getSelection();
      const nextContent = Modifier.replaceText(
        editorState.getCurrentContent(),
        selection,
        String(text || ''),
        editorState.getCurrentInlineStyle(),
      );
      const nextState = EditorState.push(editorState, nextContent, 'insert-characters');
      const cursorOffset = selection.getStartOffset() + String(text || '').length;
      const forcedSelection = selection.merge({
        anchorOffset: cursorOffset,
        focusOffset: cursorOffset,
      });
      emitChange(EditorState.forceSelection(nextState, forcedSelection));
      setTimeout(focusEditor, 0);
    },
    focus: focusEditor,
    getValue: () => htmlFromRaw(convertToRaw(editorState.getCurrentContent())),
    setImageSource: (nextSource) => {
      requestImageFromServer(nextSource);
    },
  }));

  const toggleInlineStyle = (style) => {
    emitChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType) => {
    emitChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const toolbarButton = (label, icon, onClick, active = false) => (
    <Tooltip title={label} arrow>
      <IconButton
        size="small"
        onMouseDown={(event) => {
          event.preventDefault();
          onClick();
        }}
        color={active ? 'primary' : 'default'}
        sx={{
          borderRadius: '8px',
          border: '1px solid',
          borderColor: active ? 'primary.main' : 'divider',
          backgroundColor: active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  const currentBlockType = (() => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());
    return block ? block.getType() : 'unstyled';
  })();

  const blockRendererFn = (block) => {
    if (block.getType() === 'atomic') {
      return {
        component: ImageBlock,
        editable: false,
      };
    }
    return null;
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          mb: 1,
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          backgroundColor: '#fafafa',
          flexWrap: 'wrap',
        }}
      >
        {toolbarButton('Bold', <FormatBoldIcon fontSize="small" />, () => toggleInlineStyle('BOLD'), editorState.getCurrentInlineStyle().has('BOLD'))}
        {toolbarButton('Italic', <FormatItalicIcon fontSize="small" />, () => toggleInlineStyle('ITALIC'), editorState.getCurrentInlineStyle().has('ITALIC'))}
        {toolbarButton('Underline', <FormatUnderlinedIcon fontSize="small" />, () => toggleInlineStyle('UNDERLINE'), editorState.getCurrentInlineStyle().has('UNDERLINE'))}
        {toolbarButton('Strike', <StrikethroughSIcon fontSize="small" />, () => toggleInlineStyle('STRIKETHROUGH'), editorState.getCurrentInlineStyle().has('STRIKETHROUGH'))}
        {toolbarButton('Heading', <TitleIcon fontSize="small" />, () => toggleBlockType('header-two'), currentBlockType === 'header-two')}
        {toolbarButton('Quote', <FormatQuoteIcon fontSize="small" />, () => toggleBlockType('blockquote'), currentBlockType === 'blockquote')}
        {toolbarButton('Bulleted list', <FormatListBulletedIcon fontSize="small" />, () => toggleBlockType('unordered-list-item'), currentBlockType === 'unordered-list-item')}
        {toolbarButton('Numbered list', <FormatListNumberedIcon fontSize="small" />, () => toggleBlockType('ordered-list-item'), currentBlockType === 'ordered-list-item')}
        {toolbarButton('Image', <ImageIcon fontSize="small" />, () => setImageDialogOpen(true))}
        {toolbarButton('Link', <LinkIcon fontSize="small" />, () => setLinkDialogOpen(true))}
      </Stack>

      <Box
        onClick={focusEditor}
        sx={{
          minHeight,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          backgroundColor: 'white',
          cursor: 'text',
          position: 'relative',
          '& .DraftEditor-root': {
            minHeight,
          },
          '& .DraftEditor-editorContainer': {
            minHeight,
          },
          '& .public-DraftEditor-content': {
            minHeight,
          },
        }}
      >
        {!editorState.getCurrentContent().hasText() && (
          <Typography
            sx={{
              position: 'absolute',
              left: 18,
              top: 16,
              color: 'text.disabled',
              pointerEvents: 'none',
            }}
          >
            {placeholder}
          </Typography>
        )}
        <Editor
          ref={editorRef}
          editorState={editorState}
          onChange={emitChange}
          blockRendererFn={blockRendererFn}
          placeholder=""
        />
      </Box>

      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <input
            ref={imageFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleLocalImageFile}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Image URL, local path or data URL"
            fullWidth
            value={assetUrl}
            onChange={(event) => setAssetUrl(event.target.value)}
            placeholder="https://... or C:\\path\\image.png or data:image/png;base64,..."
            sx={{ mb: 1.5 }}
          />
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button variant="outlined" onClick={triggerLocalImagePicker}>
              Upload image
            </Button>
            <Button variant="outlined" onClick={() => onBrowseImage?.()}>
              Browse image
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Uploading reads the file from your device and stores it inline. Browsing inserts the server path selected from the existing file browser.
          </Typography>
          {imageLoading && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading image preview...
              </Typography>
            </Box>
          )}
          {imageError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {imageError}
            </Typography>
          )}
          {!imageLoading && assetUrl && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <img
                src={assetUrl}
                alt="preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '260px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={insertImage} disabled={!assetUrl.trim() || imageLoading}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Link URL"
            fullWidth
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            placeholder="https://..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={insertLink} disabled={!linkUrl.trim()}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default RichTextDraftEditor;
